import { useState } from "react";
import { Crown } from "lucide-react";
import { setPrimeContactPrimary, type PrimeContact, type SetPrimaryResult } from "../api/CrmApi";
import { apiErrorMessage } from "../apiError";
import ConfirmModal from "shared/ConfirmModal";

interface Props {
  contact: Pick<PrimeContact, "id" | "is_primary" | "status" | "first_name" | "last_name" | "primary_email">;
  onChanged: (result: SetPrimaryResult) => void;
}

const nameOf = (c: Pick<PrimeContact, "first_name" | "last_name" | "primary_email">) =>
  [c.first_name, c.last_name].filter(Boolean).join(" ") || c.primary_email;

/**
 * The crown next to a contact: click to make them their company's primary contact (outreach prefers that person), click
 * again to take the mark off. When another contact already holds it, the server names them and the person confirms the
 * swap first. A contact marked Do Not Contact / Bounced cannot be primary.
 */
export default function PrimaryContactToggle({ contact, onChanged }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState<{ name: string; email: string } | null>(null);
  const flagged = contact.status !== "active";

  async function apply(isPrimary: boolean, replace = false) {
    setBusy(true);
    setError("");
    try {
      const result = await setPrimeContactPrimary(contact.id, isPrimary, replace);
      setConflict(null);
      onChanged(result);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "primary_exists") {
        setConflict({ name: detail.name, email: detail.email });
      } else {
        setConflict(null);
        setError(apiErrorMessage(err, "Could not change the primary contact."));
      }
    } finally {
      setBusy(false);
    }
  }

  const title = contact.is_primary
    ? "Primary contact — outreach uses this person first. Click to remove the mark."
    : flagged
    ? "A contact marked Do Not Contact or Bounced cannot be the primary contact"
    : "Make this the company's primary contact (outreach uses this person first)";

  return (
    <>
      <button
        type="button"
        onClick={() => apply(!contact.is_primary)}
        disabled={busy || (flagged && !contact.is_primary)}
        title={title}
        aria-label={contact.is_primary ? `Remove ${nameOf(contact)} as primary contact` : `Make ${nameOf(contact)} the primary contact`}
        aria-pressed={contact.is_primary}
        className={`p-1 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          contact.is_primary ? "text-warning hover:text-warning/80" : "text-muted-foreground/50 hover:text-warning"
        }`}
        data-testid={`primary-toggle-${contact.id}`}
      >
        <Crown className={`w-4 h-4 ${contact.is_primary ? "fill-warning" : ""}`} />
      </button>
      {error && <span className="block text-[11px] text-destructive max-w-[180px]">{error}</span>}
      {conflict && (
        <ConfirmModal
          title="Replace the primary contact?"
          confirmLabel="Replace"
          confirming={busy}
          onCancel={() => setConflict(null)}
          onConfirm={() => apply(true, true)}
          message={
            <p>
              {conflict.name} ({conflict.email}) is this company's primary contact. Making {nameOf(contact)} primary removes
              that mark from them. Outreach will use {nameOf(contact)} first.
            </p>
          }
        />
      )}
    </>
  );
}
